import { existsSync, readFileSync, realpathSync } from "node:fs";
import { createRequire } from "node:module";
import { delimiter as pathDelimiter, dirname as dirnamePath, join as joinPath } from "node:path";

const nodeFs = {
  existsSync,
  readFileSync: (p: string, enc?: string) => readFileSync(p, (enc ?? "utf8") as BufferEncoding),
  realpathSync: (p: string) => realpathSync(p),
};

/**
 * #480 Lever 2 — do-no-harm on a harness we do not control.
 *
 * AgentVault ships a passenger plugin inside a customer-operated harness. We
 * control our update stream, not theirs. On 2026-07-02 an OpenClaw update
 * (2026.4.21 -> 2026.6.11) produced a wall of config-migration errors and the
 * gateway briefly failed to start; our plugin went down with it and could not
 * self-heal from inside.
 *
 * This does not prevent that — nothing inside a plugin can fix a harness that
 * will not boot (an explicit non-goal). What it does is make OUR contribution to
 * the noise exactly one actionable line instead of a cascade, so the operator
 * can tell "AgentVault is unhappy about the harness version" apart from the
 * harness's own migration errors.
 *
 * DESIGN RULES, in priority order:
 *  1. NEVER THROW. A compat check that breaks startup is worse than the drift it
 *     reports. Every path returns a verdict or null.
 *  2. NEVER BLOCK. An out-of-range harness still loads the plugin. We are a
 *     passenger; refusing to board is not our call to make.
 *  3. SILENCE WHEN HEALTHY. A message on every boot is a message nobody reads.
 *  4. UNKNOWN IS NOT A FAILURE. If the version cannot be resolved we say nothing
 *     — guessing produces false alarms on layouts we have not seen.
 */

/** Lowest harness version this plugin is known to work against. */
export const MIN_OPENCLAW_VERSION = "2026.3.0";

/**
 * Highest harness version this plugin has actually been exercised against.
 * Above this we proceed and say so once — "untested" is not "broken", and
 * refusing to run on a newer harness would make US the outage.
 *
 * ⚠️ RAISING THIS IS A CLAIM. Only move it after exercising the plugin against
 * that harness — not because production "seems fine". 2026.7.1 looked fine for
 * weeks while all three `openclaw-compat.ts` SDK capabilities were silently
 * dead, because 2026.7.1's `exports` map stopped serving `./dist/*` and every
 * wrapper degraded quietly to a no-op.
 *
 * 2026.7.1 evidence (2026-08-05): plugin registers and runs on wren
 * ("OpenClaw managed routes also registered"); all three SDK capabilities
 * resolve via the exported `./plugin-sdk/*` subpaths; full suite green.
 *
 * 2026.10.8 evidence (2026-10-27, #1474/#1481): the channel registers and
 * starts once chatTypes says "group" (0.23.56); a fresh `create` installs,
 * enrolls and exchanges messages both ways in an isolated profile; wren runs on
 * it and replies live. The one capability still used, heartbeat wake, resolves
 * (sdk-capability-gate.test.ts, now pointed at the `openclaw` on PATH). The
 * other two were removed in #1481: they never produced a span on any version.
 */
export const TESTED_THROUGH_OPENCLAW_VERSION = "2026.10.8";

export type HarnessVerdict = "ok" | "too-old" | "untested";

export interface HarnessCompat {
  version: string;
  verdict: HarnessVerdict;
  /** Present only when the operator needs to act or know. */
  message?: string;
}

/** Compare dotted numeric versions (OpenClaw uses date-like 2026.6.11). */
export function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map((n) => parseInt(n, 10) || 0);
  const pb = b.split(".").map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

/** Minimal fs surface, injected so the walk can be tested without a real tree. */
export interface FsLike {
  existsSync(p: string): boolean;
  readFileSync(p: string, enc?: string): string;
}

/** How many directories to walk up before giving up. dist/ -> root needs 1. */
const MAX_WALK_UP = 6;

/**
 * Walk up from `startDir` looking for the `package.json` that belongs to
 * `pkgName`, and return its version.
 *
 * The name check matters: the first package.json found on the way up may belong
 * to a nested dependency, and reporting that as the harness version would be
 * worse than reporting nothing.
 */
export function findPackageVersion(
  startDir: string,
  pkgName: string,
  fsLike: FsLike,
): string | null {
  return findPackage(startDir, pkgName, fsLike)?.version ?? null;
}

/** As findPackageVersion, but also returns the package's root directory. */
export function findPackage(
  startDir: string,
  pkgName: string,
  fsLike: FsLike,
): { root: string; version: string } | null {
  try {
    let dir = startDir;
    for (let i = 0; i < MAX_WALK_UP && dir; i++) {
      const candidate = joinPath(dir, "package.json");
      if (fsLike.existsSync(candidate)) {
        try {
          const pkg = JSON.parse(fsLike.readFileSync(candidate, "utf8")) as {
            name?: string;
            version?: string;
          };
          if (pkg?.name === pkgName && typeof pkg.version === "string") {
            return { root: dir, version: pkg.version };
          }
        } catch {
          // Malformed manifest — keep walking rather than fail the boot.
        }
      }
      const up = dirnamePath(dir);
      if (up === dir) break; // fixed point: filesystem root
      dir = up;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Best-effort read of the host harness version. Returns null if unknowable.
 *
 * ⚠️ Do NOT go back to `require("openclaw/package.json")`. openclaw's `exports`
 * map does not expose that subpath, so on a real gateway install it throws
 * ERR_PACKAGE_PATH_NOT_EXPORTED and this function returns null unconditionally —
 * which silently made all of Lever 2 inert until 2026-08-05. Resolve the package
 * MAIN (which `exports` does allow) and walk up to its manifest instead.
 */
export function detectOpenClawVersion(): string | null {
  return resolveOpenClaw()?.version ?? null;
}

/** fs surface for following the `openclaw` command to its package. */
export interface PathFsLike extends FsLike {
  realpathSync(p: string): string;
}

/**
 * #1481 — find the OpenClaw package behind the `openclaw` command on PATH.
 *
 * Inside the gateway the plugin can resolve `openclaw` from its own location,
 * because OpenClaw links itself in as a peer. `npx @agentvault/agentvault
 * doctor` runs from npx's cache, where it cannot, so doctor reported the harness
 * version as unknown and every SDK capability as dead on every OpenClaw. The
 * command on PATH is the install the operator runs, so follow it: resolve the
 * symlink, then walk up to the package named `openclaw`.
 */
export function findOpenClawOnPath(
  pathEnv: string | undefined,
  fsLike: PathFsLike,
): { root: string; version: string } | null {
  for (const dir of (pathEnv ?? "").split(pathDelimiter)) {
    if (!dir) continue;
    const bin = joinPath(dir, "openclaw");
    try {
      if (!fsLike.existsSync(bin)) continue;
      const found = findPackage(dirnamePath(fsLike.realpathSync(bin)), "openclaw", fsLike);
      if (found) return found;
    } catch {
      // Unreadable entry — try the next PATH directory.
    }
  }
  return null;
}

/**
 * The OpenClaw package this process should talk about: the one the plugin can
 * resolve from where it is installed, else the one behind `openclaw` on PATH.
 * Null when neither exists. Unknown, not broken. Rule 4.
 */
export function resolveOpenClaw(): { root: string; version: string } | null {
  try {
    const mainPath = createRequire(import.meta.url).resolve("openclaw");
    const found = findPackage(dirnamePath(mainPath), "openclaw", nodeFs);
    if (found) return found;
  } catch {
    // Not a sibling of this install — fall through to PATH.
  }
  return findOpenClawOnPath(process.env.PATH, nodeFs);
}

/**
 * Evaluate the harness version. Returns null when there is nothing to say —
 * either the version is unknown, or it is inside the tested range.
 */
export function checkHarnessCompat(version: string | null = detectOpenClawVersion()): HarnessCompat | null {
  try {
    if (!version) return null;

    if (compareVersions(version, MIN_OPENCLAW_VERSION) < 0) {
      return {
        version,
        verdict: "too-old",
        message:
          `[AgentVault] This OpenClaw (${version}) is older than the minimum this ` +
          `plugin supports (${MIN_OPENCLAW_VERSION}). AgentVault will keep running, but ` +
          `errors below may be version drift rather than configuration faults — ` +
          `update OpenClaw, or pin @agentvault/agentvault to a matching release.`,
      };
    }

    if (compareVersions(version, TESTED_THROUGH_OPENCLAW_VERSION) > 0) {
      return {
        version,
        verdict: "untested",
        message:
          `[AgentVault] This OpenClaw (${version}) is newer than the version this ` +
          `plugin was tested against (${TESTED_THROUGH_OPENCLAW_VERSION}). Proceeding — ` +
          `report anything that misbehaves.`,
      };
    }

    return { version, verdict: "ok" };
  } catch {
    return null; // Rule 1: a compat check must never be the thing that breaks.
  }
}
